import { useEffect, useState } from "react";
import * as Sentry from "../../lib/errorReporting";
import BottomSheet from "../../components/BottomSheet";
import { authedApiFetch, PUBLIC_WEB_BASE } from "../../lib/api";
import { openInAppBrowser } from "../../lib/platform";
import { supabase } from "../../lib/supabase";
import { APP_VERSION, BUILD_TYPE } from "../../lib/constants";
import { JT } from "../../components/ui/jumbotronTokens";
import HelplineCard from "../../components/ui/HelplineCard";
import { canSignOutOfSportsbooks, signOutOfSportsbooks } from "../../lib/slipsync/session-reset";
import { signOutSlipTrack } from "../../lib/signOutSlipTrack";

// AccountSheet -- the signed-in user's account actions (#1079, App Store 5.1.1(v)).
// Single job: show identity, sign out, legal links, and the two-step account deletion.
// Reads: user, isOnline, canReauthorizeWithApple. Writes: nothing directly -- calls
// signOutSlipTrack (sportsbooks, then Supabase; #1415 D6), supabase.auth.getUser (only to confirm a stale-session
// deletion), openInAppBrowser, reauthorizeWithApple, POST /api/account-delete, and signOutOfSportsbooks (the
// sportsbook-only sign-out button).
// Does NOT read or write bets, groups, or any store. Legal links use PUBLIC_WEB_BASE (https):
// on iOS getOrigin() is capacitor://localhost, which openInAppBrowser drops by design.
// Apple-identity users must re-run the native Apple sheet at confirm time so the server can
// bind + revoke the Apple grant (spec D7); a code is single-use, so ANY non-200 means the
// next attempt re-prompts.

const PROVIDER_LABEL = { google: "Google", apple: "Apple" };
const SUCCESS_LINGER_MS = 1200;
// DESIGN.md Label role at the readable floor (UI shell + token floor, 2026-09-20).
const lbl = { ...JT.type.label, color: JT.text.tertiary, marginBottom: 5, display: "block" };
const rowBtn = { width: "100%", textAlign: "left", padding: "12px 14px", borderRadius: 10, border: "1px solid #1e293b", background: "#0a1628", color: "#e2e8f0", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", marginBottom: 8 };

function providerOf(user) {
  const fromMeta = user && user.app_metadata ? user.app_metadata.provider : undefined;
  const fromMetaProviders = Array.isArray(user && user.app_metadata && user.app_metadata.providers) ? user.app_metadata.providers : [];
  const fromIdentities = Array.isArray(user && user.identities) ? user.identities.map((i) => i && i.provider) : [];
  const primary = fromMeta || fromIdentities[0] || "unknown";
  return { primary, hasApple: fromMeta === "apple" || fromMetaProviders.includes("apple") || fromIdentities.includes("apple") };
}

function providerLabel(primary) {
  return Object.prototype.hasOwnProperty.call(PROVIDER_LABEL, primary) ? PROVIDER_LABEL[primary] : String(primary || "").toUpperCase();
}

export default function AccountSheet({ open, onClose, user, isOnline, reauthorizeWithApple, canReauthorizeWithApple }) {
  const [step, setStep] = useState("menu"); // menu | confirm | done
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [forceApple, setForceApple] = useState(false);
  const [sbStatus, setSbStatus] = useState(null);
  const [sbBusy, setSbBusy] = useState(false);
  const { primary, hasApple } = providerOf(user);
  const needsApple = hasApple || forceApple;
  const appleNeedsNative = needsApple && !canReauthorizeWithApple;

  useEffect(() => { if (!open) { setStep("menu"); setBusy(false); setError(null); setForceApple(false); setSbStatus(null); } }, [open]);

  const localSignOut = async () => {
    // #1415 D6: signOutSlipTrack ends the sportsbook session (iOS) before the SlipTrack one. Sportsbook failures are
    // already warnings, and the session-owner check in useSlipSync is the backstop.
    try {
      const result = await signOutSlipTrack();
      if (result && result.error) {
        Sentry.captureMessage("account-sheet: signOut failed", { level: "warning", tags: { function: "AccountSheet" } });
      }
    } catch (e) {
      Sentry.captureMessage("account-sheet: signOut failed", { level: "warning", tags: { function: "AccountSheet" } });
    }
  };

  const signOut = async () => { await localSignOut(); onClose(); };
  const sportsbookSignOut = async () => {
    setSbBusy(true); setSbStatus(null);
    const { ok } = await signOutOfSportsbooks();
    setSbBusy(false);
    setSbStatus(ok ? "Signed out of sportsbooks on this phone" : "Couldn't sign out, try again");
  };
  const openLegal = (file) => openInAppBrowser(`${PUBLIC_WEB_BASE}/${file}`);

  // A prior attempt may have completed server-side without the client seeing the 200 (network
  // drop after auth delete). The retry then refuses with auth_required / user_not_found on a
  // token that still looks valid locally. Ask Supabase whether the user still exists: gone ->
  // the deletion is done and the user must not be stranded on an error with a dead session.
  // "Gone" is narrow: gone ONLY on a GoTrue `user_not_found` error (403) or an affirmative
  // `{ user: null, error: null }` answer. GoTrue also returns 403 for `session_not_found` and
  // `user_banned` -- neither means the account was deleted. A 401 can be `bad_jwt` after a
  // secret rotation, which is also not deletion. A retryable fetch error (offline), a
  // missing-session error, or a thrown getUser is NOT evidence the account was deleted --
  // those fall through to the normal refusal path.
  const accountIsGone = async () => {
    if (!supabase || !supabase.auth || typeof supabase.auth.getUser !== "function") return false;
    try {
      const r = await supabase.auth.getUser();
      const e = r && r.error;
      if (e) {
        if (e.name === "AuthRetryableFetchError") return false;
        return e.code === "user_not_found";
      }
      return Boolean(r && r.data && !r.data.user);
    } catch (e) {
      Sentry.captureMessage("account-sheet: getUser failed", { level: "warning", tags: { function: "AccountSheet", stage: "stale-session-check" }, extra: { error: e && e.message } });
      return false;
    }
  };

  const finishDeleted = async () => {
    setStep("done");
    await new Promise((r) => setTimeout(r, SUCCESS_LINGER_MS));
    await localSignOut();
    onClose();
  };

  const confirmDelete = async () => {
    setError(null);
    setBusy(true);
    try {
      let body = {};
      if (needsApple) {
        const r = await reauthorizeWithApple();
        if (!r || !r.ok) {
          if (!(r && r.cancelled)) setError("Apple sign-in did not complete. Try again.");
          return;
        }
        body = { appleAuthorizationCode: r.authorizationCode };
      }
      let res;
      try {
        res = await authedApiFetch("/api/account-delete", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      } catch (e) {
        Sentry.captureException(e, { tags: { function: "AccountSheet", stage: "request" } });
        setError("Couldn't reach the server. Check your connection and try again.");
        return;
      }
      // Non-JSON body is reported below via the 'delete refused' warning with stage http_<status> (CC-IMPL-010 covered there).
      let payload = null;
      try { payload = await res.json(); } catch (e) { payload = null; }
      if (!res.ok || !payload || payload.deleted !== true) {
        const stage = (payload && payload.stage) || `http_${res.status}`;
        if ((stage === "auth_required" || stage === "user_not_found") && (await accountIsGone())) {
          Sentry.captureMessage("account-sheet: deletion confirmed via stale session", { level: "info", tags: { function: "AccountSheet", stage } });
          await finishDeleted();
          return;
        }
        if (stage === "apple_code_required") {
          // The server is authoritative about the account's Apple state (GPT post-impl P1-2):
          // always adopt it. On iOS the next confirm re-runs the native sheet; elsewhere
          // appleNeedsNative hides the Delete action behind the open-on-iPhone instruction.
          setForceApple(true);
          setError(canReauthorizeWithApple
            ? "Please confirm with Apple to delete this account. Tap Delete my account again."
            : "This account was created with Apple, so it can only be deleted from the iPhone app.");
        } else {
          const detail = payload && typeof payload.error === "string" ? payload.error : "Try again.";
          const applePart = needsApple ? " You'll be asked to confirm with Apple again." : "";
          setError(`Couldn't delete your account (${stage}). ${detail}${applePart}`);
        }
        Sentry.captureMessage("account-sheet: delete refused", { level: "warning", tags: { function: "AccountSheet", stage } });
        return;
      }
      await finishDeleted();
    } catch (e) {
      Sentry.captureException(e, { tags: { function: "AccountSheet", stage: "unexpected" } });
      setError("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet open={open} onClose={busy ? () => {} : onClose} locked={busy}>
      <div style={{ padding: "8px 18px 48px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <span style={{ fontSize: 17, fontWeight: 800, color: "#f1f5f9" }}>Account</span>
          <button onClick={busy ? undefined : onClose} disabled={busy} aria-label="Close" style={{ background: "none", border: "none", color: JT.text.secondary, fontSize: 20, minWidth: 44, minHeight: 44, cursor: "pointer" }}>x</button>
        </div>
        <div style={{ marginBottom: 16 }}>
          <span style={lbl}>Signed in as</span>
          <div style={{ fontSize: 14, color: "#e2e8f0", fontWeight: 600 }}>{(user && user.email) || "-"}</div>
          <div style={{ fontSize: 11, color: JT.text.tertiary }}>{providerLabel(primary)}</div>
        </div>

        {step === "menu" && (
          <>
            <button style={rowBtn} onClick={signOut}>Sign out</button>
            {canSignOutOfSportsbooks() && (
              <>
                <button style={{ ...rowBtn, minHeight: 44, opacity: sbBusy ? 0.6 : 1 }} disabled={sbBusy} onClick={sportsbookSignOut}>Sign out of sportsbooks</button>
                {sbStatus && <div role="status" style={{ fontSize: 12, color: JT.text.secondary, margin: "-2px 0 10px" }}>{sbStatus}</div>}
              </>
            )}
            {/* #1420: always visible, never behind a tap (Compliance Gate, responsible-gambling minimum). */}
            <span style={{ ...lbl, marginTop: 10 }}>Gambling help</span>
            <div style={{ marginBottom: 14 }}><HelplineCard /></div>
            <button style={rowBtn} onClick={() => openLegal("privacy.html")}>Privacy Policy</button>
            <button style={rowBtn} onClick={() => openLegal("terms.html")}>Terms</button>
            <span style={{ ...lbl, marginTop: 18, color: "#fca5a5" }}>Danger zone</span>
            <button style={{ ...rowBtn, border: "1px solid #7f1d1d", color: "#fca5a5" }} onClick={() => setStep("confirm")}>Delete account</button>
            {/* Build line -- moved here from the app header (UI shell + token floor, 2026-09-20). */}
            <div style={{ ...JT.type.label, color: JT.text.tertiary, marginTop: 18 }}>{`v${APP_VERSION} · ${BUILD_TYPE}`}</div>
          </>
        )}

        {step === "confirm" && (
          <div>
            <p style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.5 }}>
              This permanently deletes your bets, imports, and Bookies memberships. Parsed slip data is kept without your identity to improve slip reading. This cannot be undone.
            </p>
            {appleNeedsNative ? (
              <p style={{ fontSize: 13, color: "#f59e0b" }}>Open SlipTrack on your iPhone to delete an account created with Apple.</p>
            ) : (
              <>
                {needsApple && <p style={{ fontSize: 12, color: "#94a3b8" }}>You will be asked to confirm with Apple first.</p>}
                <button disabled={busy || !isOnline} aria-busy={busy} aria-label="Delete my account" style={{ ...rowBtn, border: "1px solid #b91c1c", background: "#450a0a", color: "#fecaca", opacity: busy || !isOnline ? 0.5 : 1 }} onClick={confirmDelete}>
                  {busy ? "Deleting..." : "Delete my account"}
                </button>
              </>
            )}
            <button style={rowBtn} disabled={busy} onClick={() => { setStep("menu"); setError(null); }}>Cancel</button>
            {error && <div role="alert" style={{ fontSize: 12, color: "#fca5a5", marginTop: 6 }}>{error}</div>}
            {!isOnline && <div style={{ fontSize: 12, color: "#f59e0b" }}>You're offline. Reconnect to delete your account.</div>}
          </div>
        )}

        {step === "done" && (
          <div role="status" style={{ fontSize: 14, color: "#86efac", fontWeight: 700 }}>Your account has been deleted.</div>
        )}
      </div>
    </BottomSheet>
  );
}
